import React, { useEffect, useMemo, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { RELEASES } from '../data/releases';

/**
 * "What's new" popup. Compares `localStorage['twb:lastSeenRelease']` to
 * the array in src/data/releases.ts and shows EVERY entry newer than
 * the user's last-seen version stacked in one modal — so a user who
 * skipped two releases sees both summaries the next time they refresh
 * (instead of just the latest one).
 *
 * Behavior:
 *   - First-time user (no localStorage value): shows just the most-recent
 *     entry as a single-card welcome, not the entire history. Avoids a
 *     three-screen scroll on a brand-new sign-in.
 *   - Returning user with a known version: shows everything strictly
 *     newer than that version, in newest-first order.
 *   - Returning user with an unknown version (entry pruned, or stored
 *     value doesn't appear in the current array): falls back to showing
 *     just the latest. Better than dumping the whole backlog.
 *   - Dismissing writes RELEASES[0].version → marks all current entries
 *     as seen.
 *
 * Mounted from Layout, so it only fires on authenticated routes.
 */

const STORAGE_KEY = 'twb:lastSeenRelease';

const WhatsNewModal: React.FC = () => {
  const latest = RELEASES[0];
  const [open, setOpen] = useState(false);
  const [unseen, setUnseen] = useState<typeof RELEASES>([]);

  const computedUnseen = useMemo(() => {
    if (RELEASES.length === 0) return [];
    let seen: string | null = null;
    try {
      seen = window.localStorage.getItem(STORAGE_KEY);
    } catch {
      return [];
    }
    if (seen === null) {
      // First-time user — show just the most recent entry. Reading the
      // entire history on a fresh sign-in would be intimidating.
      return [RELEASES[0]];
    }
    if (seen === RELEASES[0].version) {
      return [];
    }
    const idx = RELEASES.findIndex((r) => r.version === seen);
    if (idx === -1) {
      // Stored version isn't in the array anymore (pruned, or stale
      // localStorage from a forked branch). Fall back to "just show
      // the latest."
      return [RELEASES[0]];
    }
    // Everything between index 0 (newest) and idx-1 inclusive is
    // strictly newer than what the user has seen.
    return RELEASES.slice(0, idx);
  }, []);

  useEffect(() => {
    if (computedUnseen.length === 0) return;
    setUnseen(computedUnseen);
    // Tiny delay so the modal doesn't compete with the initial route
    // paint — gives the dashboard a moment to settle before the
    // overlay arrives.
    const t = window.setTimeout(() => setOpen(true), 350);
    return () => window.clearTimeout(t);
  }, [computedUnseen]);

  if (!latest || !open || unseen.length === 0) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, latest.version);
    } catch {
      // Private mode / storage disabled — closing the modal anyway is fine.
    }
    setOpen(false);
  };

  const headline =
    unseen.length === 1
      ? unseen[0].title
      : `What's new — ${unseen.length} updates`;
  const subline =
    unseen.length === 1
      ? `Released ${unseen[0].date}`
      : `${unseen[unseen.length - 1].date} → ${unseen[0].date}`;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="whats-new-title"
    >
      <div className="bg-white dark:bg-dark-800 rounded-xl shadow-2xl max-w-lg w-full max-h-[85vh] flex flex-col">
        <div className="p-5 border-b border-gray-200/60 dark:border-dark-700 flex items-start justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <div className="shrink-0 mt-0.5 p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
              <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-300" />
            </div>
            <div className="min-w-0">
              <h2
                id="whats-new-title"
                className="text-xl font-bold text-gray-800 dark:text-white"
              >
                {headline}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {subline}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 -mt-1 -mr-1"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-6">
          {unseen.map((release) => (
            <section key={release.version}>
              {/* Per-release section header — only shown when stacking
                  multiple entries. With a single entry the modal's main
                  header already names it, so this would be redundant. */}
              {unseen.length > 1 && (
                <div className="mb-2 flex items-baseline justify-between gap-2">
                  <h3 className="text-sm font-bold uppercase tracking-wide text-blue-700 dark:text-blue-300">
                    {release.title}
                  </h3>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {release.date}
                  </span>
                </div>
              )}
              <ul className="space-y-3">
                {release.features.map((f, i) => (
                  <li
                    key={i}
                    className="flex gap-3 text-sm text-gray-700 dark:text-gray-200"
                  >
                    <span
                      aria-hidden
                      className="shrink-0 mt-1 w-1.5 h-1.5 rounded-full bg-blue-500"
                    />
                    <span className="flex-1 leading-relaxed">{f}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="p-4 border-t border-gray-200/60 dark:border-dark-700">
          <button
            type="button"
            onClick={dismiss}
            className="w-full bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors text-sm"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsNewModal;
